import { useCallback, useRef, useState } from 'react';

/**
 * Wraps an async function and tracks its in-flight state.
 *
 * Usage:
 *   const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
 *       const result = await api.save(dto);
 *       if (result.success) onClose();
 *   });
 *
 *   <SaveButton loading={saving} onClick={handleSave}>Save</SaveButton>
 *
 * - While a call is in flight, `loading` is true and `run` is a no-op (prevents double-submits).
 * - Always resets `loading` in a finally, so failures don't leave the button stuck.
 */
export function useAsyncCallback(fn) {
    const [loading, setLoading] = useState(false);
    const fnRef = useRef(fn);
    fnRef.current = fn;

    const run = useCallback(async (...args) => {
        if (loading) return;
        setLoading(true);
        try {
            return await fnRef.current(...args);
        } finally {
            setLoading(false);
        }
    }, [loading]);

    return { run, loading };
}
